import { useEffect, useRef, useState } from "react";
import {
  creatorApprove,
  creatorSession,
  creatorStart,
  creatorTurn,
} from "./api";
import type {
  ConversationTurn,
  CreatorTurn,
  RecipePlan,
  RecipePlanNode,
} from "./types";

type Phase = "idle" | "active" | "approving" | "approved";

export function Creator() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [intent, setIntent] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conversation, setConversation] = useState<ConversationTurn[]>([]);
  const [proposedPlan, setProposedPlan] = useState<RecipePlan | null>(null);
  const [readyForApproval, setReadyForApproval] = useState(false);
  const [planRef, setPlanRef] = useState<string | null>(null);
  const [userMessage, setUserMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Scroll the conversation pane to the latest turn.
  const convoEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    convoEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation.length]);

  async function startSession() {
    if (!intent.trim()) return;
    setError(null);
    setBusy(true);
    try {
      const { session_id } = await creatorStart(intent.trim());
      setSessionId(session_id);
      setPhase("active");
      // Open the first turn with the seed prompt — the operator interprets
      // user_message + empty conversation as turn 1.
      await sendMessage(intent.trim(), session_id, []);
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  }

  async function sendMessage(
    message: string,
    sid: string | null = sessionId,
    convo: ConversationTurn[] = conversation,
  ) {
    if (!sid) return;
    setError(null);
    setBusy(true);
    // Optimistically append the user message so the UI feels responsive.
    const optimistic: ConversationTurn[] = [...convo, { role: "user", text: message }];
    setConversation(optimistic);
    try {
      const reply: CreatorTurn = await creatorTurn(sid, message);
      setConversation([...optimistic, { role: "assistant", text: reply.text }]);
      setProposedPlan(reply.proposed_plan);
      setReadyForApproval(reply.ready_for_approval);
      setUserMessage("");
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
      // Roll back the optimistic append.
      setConversation(convo);
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    if (!sessionId) return;
    setError(null);
    setPhase("approving");
    try {
      const { manifest_ref } = await creatorApprove(sessionId);
      setPlanRef(manifest_ref);
      setPhase("approved");
      // Refresh the canonical state for status display.
      try {
        const s = await creatorSession(sessionId);
        setConversation(s.conversation);
      } catch {
        /* ignore — display the local state */
      }
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
      setPhase("active");
    }
  }

  function reset() {
    setPhase("idle");
    setIntent("");
    setSessionId(null);
    setConversation([]);
    setProposedPlan(null);
    setReadyForApproval(false);
    setPlanRef(null);
    setUserMessage("");
    setError(null);
  }

  if (phase === "idle") {
    return (
      <div className="card">
        <label>What recipe do you want to build?</label>
        <textarea
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          placeholder="e.g. Build a daily SPY rebalance using momentum-based scenarios and the existing mean-variance optimizer."
        />
        {error && <div className="status-error" style={{ marginTop: 8 }}>{error}</div>}
        <div style={{ marginTop: 12 }}>
          <button disabled={busy || !intent.trim()} onClick={startSession}>
            {busy ? "Starting…" : "Start interview"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="row" style={{ alignItems: "stretch" }}>
      {/* Left: conversation */}
      <div className="card" style={{ display: "flex", flexDirection: "column" }}>
        <div className="muted" style={{ marginBottom: 8 }}>
          session · {sessionId?.slice(0, 8)}…
        </div>
        <div style={{ overflowY: "auto", maxHeight: 480, marginBottom: 8 }}>
          {conversation.map((t, i) => (
            <Bubble key={i} turn={t} />
          ))}
          <div ref={convoEndRef} />
        </div>
        {phase === "active" && (
          <>
            <textarea
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              placeholder="Your reply…"
              disabled={busy}
            />
            <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
              <button
                disabled={busy || !userMessage.trim()}
                onClick={() => sendMessage(userMessage)}
              >
                {busy ? "Sending…" : "Send"}
              </button>
              <button
                disabled={busy}
                onClick={reset}
                style={{
                  background: "transparent",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                }}
              >
                Abandon
              </button>
            </div>
          </>
        )}
        {phase === "approved" && (
          <div className="status-ok" style={{ marginTop: 8 }}>
            ✓ Plan published as <code>{planRef}</code>
            <div style={{ marginTop: 12 }}>
              <button onClick={reset}>Start a new plan</button>
            </div>
          </div>
        )}
        {error && <div className="status-error" style={{ marginTop: 8 }}>{error}</div>}
      </div>

      {/* Right: proposed plan + approve */}
      <div className="card" style={{ display: "flex", flexDirection: "column" }}>
        <div className="muted" style={{ marginBottom: 8 }}>proposed plan</div>
        {proposedPlan ? (
          <PlanPreview plan={proposedPlan} />
        ) : (
          <div className="muted">No proposal yet. Answer questions to refine.</div>
        )}
        <div style={{ marginTop: 12 }}>
          <button
            disabled={phase !== "active" || !readyForApproval || !proposedPlan}
            onClick={approve}
            title={
              readyForApproval
                ? "Publish this plan to the registry"
                : "The assistant hasn't signed off yet"
            }
          >
            {phase === "approving" ? "Approving…" : "Approve plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Bubble({ turn }: { turn: ConversationTurn }) {
  const isUser = turn.role === "user";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        margin: "6px 0",
      }}
    >
      <div
        style={{
          maxWidth: "82%",
          padding: "8px 12px",
          borderRadius: 8,
          background: isUser ? "#111827" : "#f3f4f6",
          color: isUser ? "white" : "#1f2937",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          fontSize: 13,
        }}
      >
        {turn.text}
      </div>
    </div>
  );
}

function PlanPreview({ plan }: { plan: RecipePlan }) {
  return (
    <div style={{ overflowY: "auto", maxHeight: 480 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>{plan.target.id}</strong>{" "}
        <span className="muted">v{plan.target.version}</span>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          {plan.target.description}
        </div>
      </div>
      <Tree node={plan.tree} depth={0} />
      <Bucket title="To reuse" items={plan.to_reuse} />
      <Bucket
        title="To create"
        items={[
          ...(plan.to_create?.recipes || []).map((i) => `recipe · ${i.id}`),
          ...(plan.to_create?.operators || []).map((i) => `operator · ${i.id}`),
          ...(plan.to_create?.schemas || []).map((i) => `schema · ${i.id}`),
        ]}
      />
      <Bucket
        title="Placeholders (deferred)"
        items={(plan.placeholders || []).map((i) => `${i.id} — ${i.description}`)}
      />
    </div>
  );
}

function Tree({ node, depth }: { node: RecipePlanNode; depth: number }) {
  const indent = depth * 14;
  const badge = node.exists_in_registry
    ? { text: "registered", color: "#059669" }
    : node.placeholder
      ? { text: "placeholder", color: "#9ca3af" }
      : { text: "to create", color: "#d97706" };
  return (
    <div style={{ marginLeft: indent, padding: "2px 0", fontSize: 13 }}>
      <code className={`kind-${node.kind}`}>{node.kind}</code>{" "}
      <code>{node.id}</code>{" "}
      <span style={{ color: badge.color, fontSize: 11 }}>· {badge.text}</span>
      {node.description && (
        <div className="muted" style={{ fontSize: 11, marginLeft: 14 }}>
          {node.description}
        </div>
      )}
      {(node.depends_on || []).map((child, i) => (
        <Tree key={`${child.id}-${i}`} node={child} depth={depth + 1} />
      ))}
    </div>
  );
}

function Bucket({ title, items }: { title: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
        {title}
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
        {items.map((s, i) => (
          <li key={i}>
            <code>{s}</code>
          </li>
        ))}
      </ul>
    </div>
  );
}
